import type { ProductEditor, ProductRemoval } from "@cleanbudget/contract/product";
import { useToast } from "@cleanbudget/ui/components/toast";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { match } from "ts-pattern";
import { apiErrorTag } from "@/lib/api-error";
import { orpc } from "@/lib/orpc";
import { useProductErrorMessage } from "./product-errors";

/** The product editor's data: one query, which every write replaces with the editor it answers. */
export const productQuery = (productId: string) => orpc.product.get.queryOptions({ input: { productId } });

/**
 * Every write the editor makes. Each answers the whole `ProductEditor`, which replaces the cached
 * one, so the page never reloads or merges by hand; the product list is marked stale. A
 * `ProductChanged` (two writes raced) and `ProductArchived` (archived meanwhile) reload the product.
 */
export function useProductWrites(productId: string) {
	const queryClient = useQueryClient();
	const key = productQuery(productId).queryKey;

	const show = (product: ProductEditor) => {
		queryClient.setQueryData(key, product);
		void queryClient.invalidateQueries({ queryKey: orpc.product.list.key() });
	};
	/** What to reload after a refusal: the product, or a list something was deleted from meanwhile. */
	const onError = (error: unknown) => {
		const stale = match(apiErrorTag(error))
			// Archived meanwhile (spec, A2): the page then shows it, read-only.
			.with("ProductChanged", "ProductArchived", () => key)
			.with("UnknownCategory", () => orpc.category.key())
			.with("UnknownAttributeValue", () => orpc.attribute.key())
			.with("UnknownHolder", () => orpc.holder.key())
			.otherwise(() => undefined);
		if (stale) void queryClient.invalidateQueries({ queryKey: stale });
	};
	const editor = { onSuccess: show, onError };
	const removal = { onSuccess: (result: ProductRemoval) => show(result.product), onError };
	/** Groups, values and fits change the counts on their own pages too. */
	const classification = {
		onSuccess: (product: ProductEditor) => {
			show(product);
			void queryClient.invalidateQueries({ queryKey: orpc.category.key() });
			void queryClient.invalidateQueries({ queryKey: orpc.attribute.key() });
			void queryClient.invalidateQueries({ queryKey: orpc.holder.key() });
		},
		onError,
	};

	return {
		update: useMutation(orpc.product.update.mutationOptions(editor)),
		addOption: useMutation(orpc.product.addOption.mutationOptions(editor)),
		renameOption: useMutation(orpc.product.renameOption.mutationOptions(editor)),
		removeOption: useMutation(orpc.product.removeOption.mutationOptions(removal)),
		restoreOption: useMutation(orpc.product.restoreOption.mutationOptions(editor)),
		reorderOptions: useMutation(orpc.product.reorderOptions.mutationOptions(editor)),
		addOptionValue: useMutation(orpc.product.addOptionValue.mutationOptions(editor)),
		renameOptionValue: useMutation(orpc.product.renameOptionValue.mutationOptions(editor)),
		removeOptionValue: useMutation(orpc.product.removeOptionValue.mutationOptions(removal)),
		restoreOptionValue: useMutation(orpc.product.restoreOptionValue.mutationOptions(editor)),
		reorderOptionValues: useMutation(orpc.product.reorderOptionValues.mutationOptions(editor)),
		swapOptionValues: useMutation(orpc.product.swapOptionValues.mutationOptions(editor)),
		updateVariant: useMutation(orpc.product.updateVariant.mutationOptions(editor)),
		deleteVariant: useMutation(orpc.product.deleteVariant.mutationOptions(editor)),
		restoreVariant: useMutation(orpc.product.restoreVariant.mutationOptions(editor)),
		reorderPhotos: useMutation(orpc.product.reorderPhotos.mutationOptions(editor)),
		updatePhoto: useMutation(orpc.product.updatePhoto.mutationOptions(editor)),
		deletePhoto: useMutation(orpc.product.deletePhoto.mutationOptions(editor)),
		setCategories: useMutation(orpc.product.setCategories.mutationOptions(classification)),
		setAttributeValues: useMutation(orpc.product.setAttributeValues.mutationOptions(classification)),
		setVariantAttributeValues: useMutation(
			orpc.product.setVariantAttributeValues.mutationOptions(classification),
		),
		setVariantFits: useMutation(orpc.product.setVariantFits.mutationOptions(classification)),
	};
}

export type ProductWrites = ReturnType<typeof useProductWrites>;

/**
 * Runs a write whose failure has no field to show on (a switch, a chip, an undo): a refusal becomes
 * a toast in Dutch.
 */
export function useRunWrite() {
	const toast = useToast();
	const errorMessage = useProductErrorMessage();
	return async <T>(write: Promise<T>): Promise<T | undefined> => {
		try {
			return await write;
		} catch (error) {
			toast.add({ title: errorMessage(error), priority: "high" });
			return undefined;
		}
	};
}
